import React from 'react';
import { Play, Zap, Gauge, Droplets, Award, CheckCircle2, Phone, MapPin, Mail, ArrowRight, X, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { useState, useRef } from 'react';

// Custom Video Player with proper fullscreen modal
const VideoPlayer = ({ src, poster, className }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const fullscreenVideoRef = useRef(null);

  const openFullscreen = () => {
    setIsFullscreen(true);
    // Sync video time when opening fullscreen
    if (videoRef.current && fullscreenVideoRef.current) {
      fullscreenVideoRef.current.currentTime = videoRef.current.currentTime;
    }
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    // Pause fullscreen video when closing
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
  };

  return (
    <>
      {/* Inline Video */}
      <div className={`relative group ${className || ''}`}>
        <video
          ref={videoRef}
          src={src}
          controls
          className="w-full h-full object-contain bg-black"
          poster={poster}
          playsInline
        >
          Your browser does not support the video tag.
        </video>
        {/* Custom fullscreen button overlay */}
        <button
          onClick={openFullscreen}
          className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Open in fullscreen"
        >
          <Maximize2 size={20} />
        </button>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={closeFullscreen}
        >
          {/* Close button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-50 transition-colors"
          >
            <X size={28} />
          </button>
          
          {/* Video container with 16:9 aspect ratio */}
          <div 
            className="w-full h-full max-w-[177.78vh] max-h-[56.25vw] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={fullscreenVideoRef}
              src={src}
              controls
              autoPlay
              className="w-full h-full object-contain"
              poster={poster}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </>
  );
};

const Home = () => {
  const [showFullPdpText, setShowFullPdpText] = useState(false);
  const [showFullEnergyText, setShowFullEnergyText] = useState(false);
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section with Video Introduction */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Industrial Tech Graphics Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-cyan-50">
          {/* Top right corner - Circuit board pattern */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-[0.07]">
            <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
              <g stroke="#0ea5e9" strokeWidth="2" fill="none">
                {/* Circuit lines */}
                <path d="M50,50 L150,50 L150,150 L250,150 L250,250" />
                <path d="M100,100 L200,100 L200,200 L300,200" />
                <path d="M150,50 L150,20 M250,150 L250,120" />
                <circle cx="150" cy="50" r="8" fill="#0ea5e9" />
                <circle cx="250" cy="150" r="8" fill="#0ea5e9" />
                <circle cx="150" cy="150" r="6" fill="#0ea5e9" />
                <rect x="140" y="90" width="20" height="20" fill="#0ea5e9" />
                <rect x="240" y="190" width="20" height="20" fill="#0ea5e9" />
              </g>
            </svg>
          </div>
          {/* Bottom left corner - Pressure gauge/compressor */}
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] opacity-[0.06]">
            <svg viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
              <g stroke="#0284c7" strokeWidth="3" fill="none">
                {/* Pressure gauge */}
                <circle cx="150" cy="150" r="80" />
                <circle cx="150" cy="150" r="70" />
                <path d="M150,80 L150,90 M150,210 L150,220 M80,150 L90,150 M210,150 L220,150" />
                <path d="M100,100 L110,110 M200,100 L190,110 M100,200 L110,190 M200,200 L190,190" />
                <line x1="150" y1="150" x2="150" y2="90" stroke="#0ea5e9" strokeWidth="4" />
                {/* Pipes */}
                <rect x="140" y="20" width="20" height="60" fill="#0ea5e9" opacity="0.3" />
                <rect x="20" y="140" width="60" height="20" fill="#0ea5e9" opacity="0.3" />
              </g>
            </svg>
          </div>
          {/* Subtle dot grid */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(14, 165, 233) 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:items-start">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full text-cyan-700 text-sm font-semibold">
                  Patent Pending Technology
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight">
                Sub-Zero
                <span className="block bg-gradient-to-r from-cyan-600 to-blue-700 !leading-[6rem] bg-clip-text text-transparent">
                  Refrigerated Air Dryer
                </span>
              </h1>
              <p className="text-xl text-slate-700 leading-relaxed">
                Achieve unprecedented <span className="text-cyan-600 font-semibold">-10°C pressure dew point</span> with our revolutionary technology. Remove <span className="text-cyan-600 font-semibold">22% more condensate</span> and save <span className="text-cyan-600 font-semibold">25% energy</span> compared to conventional dryers.
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg hover:border-cyan-400 hover:shadow-lg transition-all">
                  <div className="text-3xl font-bold text-cyan-600 flex items-center justify-center">-10°C</div>
                  <div className="text-sm text-slate-600 mt-1">Sub-Zero PDP</div>
                </div>
                <div className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg hover:border-cyan-400 hover:shadow-lg transition-all">
                  <div className="text-3xl font-bold text-cyan-600 flex items-center justify-center">25%</div>
                  <div className="text-sm text-slate-600 mt-1">Energy Saving</div>
                </div>
                <div className="text-center p-4 bg-white border-2 border-slate-200 rounded-lg hover:border-cyan-400 hover:shadow-lg transition-all">
                  <div className="text-3xl font-bold text-cyan-600 flex items-center justify-center">22%</div>
                  <div className="text-sm text-slate-600 mt-1">Higher Condensate Removal</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-6 text-lg group shadow-lg shadow-cyan-600/30">
                  Request a Quote
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
                <Button
                  onClick={() => document.getElementById('technology')?.scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-cyan-600 hover:border-cyan-400 px-8 py-6 text-lg">
                  Learn More
                </Button>
              </div>
            </div>

            {/* Right Content - Product Videos and Images */}
            <div className="relative space-y-4">
              {/* Product Introduction Video */}
              <Card className="bg-slate-900 border-2 border-slate-300 rounded-2xl overflow-hidden shadow-xl">
                <div className="aspect-video bg-slate-900 relative">
                  <VideoPlayer
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/e86xh5hb_Product%20Video%20Final.mp4"
                    poster=""
                    className="w-full h-full"
                  />
                </div>
              </Card>

              {/* Product Thumbnails - video first, then 3 images */}
              <div className="grid grid-cols-4 gap-2 max-w-2xl mx-auto">
                <div
                  className="aspect-square bg-slate-900 border-2 border-slate-300 rounded-lg overflow-hidden cursor-pointer hover:border-cyan-500 hover:shadow-lg transition-all duration-300 group relative"
                  onClick={() => {
                    window.open('https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/8ai69aiu_Manufacturing%20video%20final%20Compressed.mp4', '_blank');
                  }}
                >
                  <video
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/8ai69aiu_Manufacturing%20video%20final%20Compressed.mp4"
                    className="w-full h-full object-cover"
                    muted
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30">
                    <Play className="text-white drop-shadow-lg" size={24} fill="currentColor" />
                  </div>
                </div>
                <div
                  className="aspect-square bg-white border-2 border-slate-300 rounded-lg overflow-hidden cursor-pointer hover:border-cyan-500 hover:shadow-lg transition-all duration-300 group"
                  onClick={() => {
                    window.open('https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/r44v9l4k_1.png', '_blank');
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/r44v9l4k_1.png"
                    alt="Sub-Zero Dryer View 1"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div
                  className="aspect-square bg-white border-2 border-slate-300 rounded-lg overflow-hidden cursor-pointer hover:border-cyan-500 hover:shadow-lg transition-all duration-300 group"
                  onClick={() => {
                    window.open('https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/0k086kfj_2.png', '_blank');
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/0k086kfj_2.png"
                    alt="Sub-Zero Dryer View 2"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div
                  className="aspect-square bg-white border-2 border-slate-300 rounded-lg overflow-hidden cursor-pointer hover:border-cyan-500 hover:shadow-lg transition-all duration-300 group"
                  onClick={() => {
                    window.open('https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/rk9vhcpn_3.png', '_blank');
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/rk9vhcpn_3.png"
                    alt="Sub-Zero Dryer View 3"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-300 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-cyan-500 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              About <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Us</span>
            </h2>
          </div>

          <div className="space-y-8">
            {/* Main Intro */}
            <div className="text-center">
              <h3 className="text-3xl font-bold text-slate-900 mb-4">
                Engineering the Future of Compressed Air
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                At Sub-Zero Dryers, we don't just build equipment; we engineer reliability. As a premier turnkey contract manufacturer, we specialize in the design, fabrication, and rigorous testing of high-performance refrigerated air dryers for the world's most demanding compressed air systems.
              </p>
            </div>

            {/* Innovation Section */}
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 hover:border-cyan-400 hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full mr-4"></span>
                Innovation Meets Integration
              </h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                Our facility is designed to be an extension of your own engineering team. We offer a unique dual-path approach to manufacturing:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-cyan-200 rounded-lg p-6">
                  <div className="flex items-start space-x-3 mb-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                    <h4 className="text-xl font-bold text-slate-900">Sub-Zero™ Innovation</h4>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Leverage our Patent Pending Sub-Zero Technology to provide your customers with industry-leading sub-zero pressure dew point stability and energy efficiency.
                  </p>
                </div>
                
                <div className="bg-white border-2 border-cyan-200 rounded-lg p-6">
                  <div className="flex items-start space-x-3 mb-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                    <h4 className="text-xl font-bold text-slate-900">Custom-Spec Manufacturing</h4>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Already have a design? We build to your exact specifications, ensuring every unit meets your brand's proprietary standards for quality and performance.
                  </p>
                </div>
              </div>
            </div>

            {/* Why Partners Choose Us */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full mr-4"></span>
                Why Partners Choose Us
              </h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                In the world of compressed air, downtime is not an option. That's why our process is rooted in <span className="font-semibold text-cyan-600">Precision</span>, <span className="font-semibold text-cyan-600">Transparency</span>, and <span className="font-semibold text-cyan-600">Performance</span>. From the first braze to the final pressure test, we handle the entire lifecycle of production so you can focus on scaling your business.
              </p>
            </div>

            {/* Mission Statement */}
            <div className="text-center bg-slate-900 text-white rounded-2xl p-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/10 to-blue-600/10"></div>
              <div className="relative z-10">
                <p className="text-sm uppercase tracking-wider text-cyan-400 mb-3 font-semibold">Our Mission</p>
                <p className="text-xl md:text-2xl leading-relaxed font-medium">
                  To be the world's most trusted manufacturing partner for refrigerated air dryer technology, delivering "plug-and-play" solutions that exceed global industrial standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Novel Technology Section */}
      <section id="technology" className="py-24 bg-slate-50 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Novel <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Technology</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Two revolutionary innovations that set Sub-Zero apart from conventional refrigerated air dryers
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Sub-Zero PDP Technology */}
            <Card className="bg-white border-2 border-slate-200 p-8 hover:border-cyan-400 hover:shadow-xl transition-all duration-300 group">
              <div className="flex items-start space-x-4 mb-6">
                <div className="p-3 bg-cyan-50 border-2 border-cyan-200 rounded-lg group-hover:bg-cyan-100 transition-colors">
                  <Droplets className="text-cyan-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Sub-Zero PDP Technology</h3>
                  <div className="inline-block px-3 py-1 bg-cyan-50 border border-cyan-200 rounded-full text-cyan-700 text-xs font-semibold">
                    PATENT PENDING
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-slate-700 leading-relaxed">
                  Our breakthrough technology achieves <span className="text-cyan-600 font-semibold">pressure dew point down to -10°C</span>, compared to the industry standard of +3°C minimum.
                </p>

                <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-600">Conventional Dryers</span>
                    <span className="text-slate-500 font-bold">+3°C</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full mb-6">
                    <div className="w-1/3 h-full bg-slate-400 rounded-full"></div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-cyan-600 font-semibold">Sub-Zero Technology</span>
                    <span className="text-cyan-600 font-bold">-10°C</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full">
                    <div className="w-full h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full"></div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 text-cyan-600">
                  <CheckCircle2 size={20} />
                  <span className="font-semibold">22% Higher Condensate Removal</span>
                </div>
                <div className="text-slate-600 text-sm leading-relaxed">
                  <p>
                    Sub-Zero PDP is achievable with our innovative Patent Pending brazed Aluminium heat exchanger design that allows simultaneous removal of water and accumulation of ice during sub-zero conditions, without causing any issues to the heat exchanger or the dryer system.
                    {showFullPdpText && (
                      <span> The heat exchanger's air to air reheater section is thermally connected to the refrigerant cooler core and this helps us to achieve maximum condensate removal at sub-zero PDP.</span>
                    )}
                  </p>
                  <button
                    onClick={() => setShowFullPdpText(!showFullPdpText)}
                    className="text-cyan-600 font-semibold hover:text-cyan-700 mt-2 inline-flex items-center"
                  >
                    {showFullPdpText ? 'Read Less' : 'Read More'}
                    <ArrowRight className={`ml-1 transition-transform ${showFullPdpText ? 'rotate-90' : ''}`} size={16} />
                  </button>
                </div>
              </div>
            </Card>

            {/* Energy Saving Technology */}
            <Card className="bg-white border-2 border-slate-200 p-8 hover:border-cyan-400 hover:shadow-xl transition-all duration-300 group">
              <div className="flex items-start space-x-4 mb-6">
                <div className="p-3 bg-cyan-50 border-2 border-cyan-200 rounded-lg group-hover:bg-cyan-100 transition-colors">
                  <Zap className="text-cyan-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Intelligent Energy Efficiency</h3>
                  <div className="inline-block px-3 py-1 bg-cyan-50 border border-cyan-200 rounded-full text-cyan-700 text-xs font-semibold">
                    AUTO ON-OFF SYSTEM
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-slate-700 leading-relaxed">
                  Microcontroller-based Auto ON-OFF system intelligently manages power consumption based on real-time demand.
                </p>
                
                <div className="text-slate-600 text-sm leading-relaxed">
                  <p>
                    In a typical industrial environment, the air compressor's full capacity is often not completely used. Frequent partial or no air demands occur during working hours (for e.g. lunch time, system maintenance and other breaks).
                    {showFullEnergyText && (
                      <span> The intelligent micro-controller system saves energy by switching OFF the compressor during low load conditions and switching it back ON as demand comes back. This results in 25% energy savings compared to regular non-cycling dryer and also increases the system lifespan.</span>
                    )}
                  </p>
                  <button
                    onClick={() => setShowFullEnergyText(!showFullEnergyText)}
                    className="text-cyan-600 font-semibold hover:text-cyan-700 mt-2 inline-flex items-center"
                  >
                    {showFullEnergyText ? 'Read Less' : 'Read More'}
                    <ArrowRight className={`ml-1 transition-transform ${showFullEnergyText ? 'rotate-90' : ''}`} size={16} />
                  </button>
                </div>

                <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-6 space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-slate-900 font-semibold mb-1">Automatic Shutdown</p>
                      <p className="text-slate-600 text-sm">System switches off during low demand periods</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-slate-900 font-semibold mb-1">Smart Restart</p>
                      <p className="text-slate-600 text-sm">Automatically restarts when demand is back and ensures clean dry air is available throughout the energy saving cycle</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-slate-900 font-semibold mb-1">Minimal Pressure Drop</p>
                      <p className="text-slate-600 text-sm">Air-side pressure drop does not exceed 0.15 barg</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-lg p-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-cyan-600 mb-2">25%</div>
                    <p className="text-slate-900 font-semibold mb-1">More Energy Efficient</p>
                    <p className="text-slate-600 text-sm">Compared to any other dryer in the market</p>
                  </div>
                </div>

                <p className="text-slate-400 text-sm">
                  Perfect for both air compressor OEMs and retro-fit custom solutions, ensuring maximum sustainability.
                </p>
              </div>
            </Card>
          </div>

          {/* Target Industries */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">Trusted by Leading Industries</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
              'Air Compressor OEM',
              'Pneumatic Equipment Distributors',
              'Laser Cutting Machines',
              'Pharmaceutical Equipment',
              'Food Packaging',
              'Powder Coating Solutions',
              'Automotive Manufacturing',
              'Industrial Pneumatic Tools'].
              map((industry, index) =>
              <div
                key={index}
                className="bg-white border-2 border-slate-200 rounded-lg p-4 text-center hover:border-cyan-400 hover:shadow-lg transition-all duration-300">

                  <p className="text-slate-700 text-sm font-medium">{industry}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications Section */}
      <section id="specs" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Technical <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Specifications</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive range of models to suit every application requirement
            </p>
          </div>

          {/* Download Brochure Section */}
          <div className="mb-16">
            <Card className="bg-slate-50 border-2 border-slate-200 p-12 text-center hover:border-cyan-400 hover:shadow-xl transition-all">
              <div className="max-w-2xl mx-auto space-y-6">
                <div className="w-20 h-20 mx-auto bg-cyan-50 border-2 border-cyan-300 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-3">Product Specifications</h3>
                  <p className="text-slate-600 text-lg">
                    Download our comprehensive technical brochure with detailed specifications, correction factors, and model comparisons for all Sub-Zero dryer ranges.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    // Download the technical brochure PDF
                    const link = document.createElement('a');
                    link.href = 'https://customer-assets.emergentagent.com/job_2fd1a4d1-e7bc-4a2a-8b1e-2333423306c8/artifacts/bgd4ah6c_Brochure-SubZero%20Ref%20Dryer.pdf';
                    link.download = 'Brochure-SubZero Ref Dryer.pdf';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-6 text-lg group shadow-lg shadow-cyan-600/30">

                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Technical Brochure
                </Button>
                <p className="text-slate-500 text-sm">
                  PDF Format • Complete Product Range • Correction Factors Included
                </p>
              </div>
            </Card>
          </div>

          {/* Standard Features */}
          <div>
            <h3 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Standard Features <span className="text-cyan-600">Across All Models</span>
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Patent Pending Brazed Aluminium Evaporator */}
              <Card className="bg-white border-2 border-slate-200 overflow-hidden group hover:border-cyan-400 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square bg-slate-50 flex items-center justify-center p-8">
                  <img
                    src="/assets/brazed-aluminium-evaporator.png"
                    alt="Brazed Aluminium Evaporator"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-cyan-50 border border-cyan-200 rounded-full text-cyan-700 text-xs font-semibold mb-3">
                    PATENT PENDING
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Brazed Aluminium Evaporator</h4>
                  <p className="text-slate-600 mb-4">
                    Advanced Patent Pending heat exchanger design engineered for maximum efficiency and durability.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">20 barg Working Pressure</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">3 in 1 Integrated Design (Reheater/ Cooler/ Demister)</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">SS 304 Mesh Demister</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">0.15 bar(g) pressure drop</span>
                    </li>
                  </ul>
                </div>
              </Card>

              {/* High Contrast OLED Display */}
              <Card className="bg-white border-2 border-slate-200 overflow-hidden group hover:border-cyan-400 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square bg-slate-50 flex items-center justify-center p-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/xtijm8e5_background-replacer-result.png"
                    alt="OLED Display"
                    className="w-full h-full object-contain" />

                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-slate-900 mb-3">High Contrast OLED Display</h4>
                  <p className="text-slate-600 mb-4">
                    Crystal-clear real-time monitoring with intuitive interface for precise control.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Real-time PDP monitoring</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Service alarm notifications</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Compressor cycling protection</span>
                    </li>
                  </ul>
                </div>
              </Card>

              {/* Timer Drain Valve */}
              <Card className="bg-white border-2 border-slate-200 overflow-hidden group hover:border-cyan-400 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square bg-slate-50 flex items-center justify-center p-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/1ibi45a4_Untitled-Photoroom.png"
                    alt="Timer Drain Valve"
                    className="w-full h-full object-contain" />

                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Timer Drain Valve</h4>
                  <p className="text-slate-600 mb-4">
                    Automated condensate drainage system for reliable, maintenance-free operation.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Programmable intervals</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Zero air loss</span>
                    </li>
                    <li className="flex items-center space-x-2 text-slate-700">
                      <CheckCircle2 className="text-cyan-600" size={16} />
                      <span className="text-sm">Manual test function</span>
                    </li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Import and render remaining sections */}
      <ContractManufacturingSection />
      <ContactUsSection />
      
      <Footer />
    </div>);

};

// Import sections
import { ContractManufacturing as ContractManufacturingSection, ContactSection as ContactUsSection } from './Sections';

export default Home;